import { createFileRoute } from "@tanstack/react-router";
import type { ClientStudioDocMetadata } from "api-types/studioDocs/studioDocMetadataTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import CustomerInfo from "lib/components/builders/communities/plans/homePlans/CustomerInfo";
import ExportToErp from "lib/components/builders/communities/plans/homePlans/ExportToErp";
import HomePlanActivity from "lib/components/builders/communities/plans/homePlans/HomePlanActivity";
import HomePlanHighlights from "lib/components/builders/communities/plans/homePlans/HomePlanHighlights";
import HomePlanLayout from "lib/components/builders/communities/plans/homePlans/HomePlanLayout";
import PlanDocuments from "lib/components/builders/communities/plans/homePlans/PlanDocuments";
import PlanPhases from "lib/components/builders/communities/plans/homePlans/PlanPhases";
import PlanSalesStaff from "lib/components/builders/communities/plans/homePlans/PlanSalesStaff";
import StudioFileInfo from "lib/components/builders/communities/plans/homePlans/StudioFileInfo";
import UnitPlanInfo, { getUnitName } from "lib/components/builders/communities/plans/homePlans/UnitPlanInfo";
import UnitPlanStatus from "lib/components/builders/communities/plans/homePlans/UnitPlanStatus";
import UnitSourcesTable from "lib/components/builders/communities/plans/homePlans/UnitSourcesTable";
import { useMultiUnitHomePlans } from "lib/components/builders/communities/plans/homePlans/useHomePlanAPI";
import { getSortedReportingUnits } from "lib/components/builders/communities/plans/planUtils";
import { userCanEditHomePlan } from "lib/security/client";
import { HomePlanContext } from "lib/state/HomePlanContextProvider";
import { useUserContext } from "lib/state/UserContextProvider";
import { canDownloadSomePlanSets } from "lib/utils/planDocumentPermissions";
import { useContext, useMemo } from "react";
import withAuthentication from "../../../../../components/withAuthentication";
import styles from "./index.module.scss";

function HomePlanView() {
  const {
    homePlan,
    locationPlan,
    lot,
    updateHomePlan,
    location,
    masterPlan,
    studioDocMetaData,
    lotDiscounts,
    homePlanDiscounts,
  } = useContext(HomePlanContext);
  const { user, builder, hasPermission, isSuperAdmin, builderUser } = useUserContext();
  assertIsNotUndefined(builder);
  const salesAgent = homePlan?.salesStaff ?? location?.salesStaff ?? null;
  const salesAgentSource = homePlan?.salesStaff ? "Home Plan" : "Location";
  const studioFile = homePlan?.doc;

  const isMultiUnitHomePlan = !!homePlan?.isMultiUnit;
  const isMultiUnitLocation = location?.locationLevel?.type === "multiunit";
  const isUnitPlan = isMultiUnitLocation && !isMultiUnitHomePlan;

  // Must fetch before early return
  const { data: multiUnitPlans } = useMultiUnitHomePlans({
    builderId: builder.id,
    locationId: location?.id ?? "",
    enabled: isUnitPlan && !!location?.id,
  });

  const buildingHomePlan = useMemo(() => {
    if (homePlan?.unitSource?.multiUnitHomePlanId) {
      return multiUnitPlans?.find((hp) => hp.id === homePlan.unitSource?.multiUnitHomePlanId);
    }
    return multiUnitPlans?.find((hp) => hp.isMultiUnit);
  }, [homePlan?.unitSource?.multiUnitHomePlanId, multiUnitPlans]);

  const buildingHomePlanId = buildingHomePlan?.id;
  const buildingPlanSetId = buildingHomePlan?.planSetId;

  if (!homePlan) {
    return null;
  }

  const canEdit = userCanEditHomePlan({ hasPermission, user }, homePlan, location);

  // Higharc admins can still download plans regardless
  const planDownloadDisabled = !(
    isSuperAdmin ||
    (masterPlan?.planDownloadEnabled && locationPlan?.planDownloadEnabled)
  );

  const planDownloadAllowed = canDownloadSomePlanSets({
    hasPermission,
    isSuperAdmin,
    userId: user?.id,
    homePlanSalesAgentId: homePlan.salesStaffId,
    locationSalesAgentId: location?.salesStaffId,
    planSetTemplates: homePlan.doc?.planSetTemplates ?? {},
    userRoles: builderUser?.roles,
  });

  const reportingUnits = getSortedReportingUnits(masterPlan);

  // Get unit name from lot's reportingUnitId or from unitSource if linked
  const unitReportingUnitId = homePlan.unitSource?.reportingUnitId ?? lot?.reportingUnitId ?? undefined;
  const unitName = isUnitPlan ? getUnitName(unitReportingUnitId, reportingUnits) : "";

  const renderPlanTypeSection = () => {
    if (isMultiUnitHomePlan) {
      return (
        <>
          <PlanPhases
            location={location}
            homePlan={homePlan}
            builder={builder}
            updateHomePlan={updateHomePlan}
            locationPlan={locationPlan}
          />
          {reportingUnits.length > 0 && (
            <UnitSourcesTable homePlan={homePlan} builderId={builder.id} reportingUnits={reportingUnits} />
          )}
        </>
      );
    }
    if (isUnitPlan) {
      return (
        <>
          {reportingUnits.length > 0 && (
            <UnitPlanInfo
              unitName={unitName}
              buildingHomePlanId={buildingHomePlanId}
              buildingPlanSetId={buildingPlanSetId}
              builderId={builder.id}
            />
          )}
          <UnitPlanStatus location={location} homePlan={homePlan} builder={builder} updateHomePlan={updateHomePlan} />
        </>
      );
    }
    return (
      <PlanPhases
        location={location}
        homePlan={homePlan}
        builder={builder}
        updateHomePlan={updateHomePlan}
        locationPlan={locationPlan}
      />
    );
  };

  return (
    <div className={styles.homePlanView}>
      <div className={styles.leftColumn}>
        {renderPlanTypeSection()}
        <HomePlanActivity
          homePlan={homePlan}
          planDownloadDisabled={planDownloadDisabled}
          planDownloadAllowed={planDownloadAllowed}
        />
      </div>
      <div className={styles.rightColumn}>
        <HomePlanHighlights
          masterPlan={masterPlan}
          locationPlan={locationPlan}
          homePlan={homePlan}
          lot={lot}
          builder={builder}
          hasPermissions={hasPermission("VIEW_HOME_PLANS")}
        />
        {hasPermission("EDIT_HOME_PLANS") && <ExportToErp homePlanId={homePlan.id} />}
        {studioFile && (
          <StudioFileInfo
            studioFileName={studioFile?.name}
            studioDocMetadata={studioDocMetaData as ClientStudioDocMetadata}
          />
        )}
        {!isMultiUnitHomePlan && <CustomerInfo homePlanId={homePlan.id} homePlan={homePlan} canEdit={canEdit} />}
        <PlanSalesStaff salesStaff={salesAgent} source={salesAgentSource} />
        <PlanDocuments
          lot={lot}
          homePlan={homePlan}
          locationPlan={locationPlan}
          location={location}
          masterPlan={masterPlan}
          builder={builder}
          planDownloadDisabled={planDownloadDisabled}
          planDownloadAllowed={planDownloadAllowed}
          lotDiscounts={lotDiscounts}
          homePlanDiscounts={homePlanDiscounts}
          isMultiUnitLocation={isMultiUnitLocation}
        />
      </div>
    </div>
  );
}

// TODO: Clean this up. Set up this way because HomePlanLayout provides
// the HomePlanContext for HomePlanView
function HomePlanPage() {
  return (
    <HomePlanLayout currentPage="overview">
      <HomePlanView />
    </HomePlanLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/home-plans/$home_plan_id/")({
  component: withAuthentication(HomePlanPage, { permissions: ["VIEW_HOME_PLANS"] }),
});
